import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  mergeClasses,
  Spinner,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { getCategories } from "api";
import { useSharedDialogContentStyles } from "theme/sharedStyles";
import { ConstantValues } from "common/constants";
import { IAccessLLMModel } from "models/IAccessLLMModel";
import { ICategory } from "models/ICategory";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { formatDate, getModelByKey } from "utils/generalUtils";
import { TabValues } from "../editDialog/EditUseCasePrompt.types";
import { getKnowledgeComponentsLabel } from "../useCasePromptUtils";
import { useStyles } from "./ViewUseCasePrompt.styles";
import {
  EMPTY_VALUE,
  IViewUseCasePromptProps,
  LABEL_VIEW,
} from "./ViewUseCasePrompt.types";

export const ViewUseCasePrompt = (
  props: IViewUseCasePromptProps,
): JSX.Element => {
  const styles = useStyles();
  const sharedContentStyles = useSharedDialogContentStyles();
  const models: IAccessLLMModel[] = useSelector(
    (state: RootState) => state.accessLLMModelsSlice.value,
  );
  const useCases = useSelector((state: RootState) => state.useCasesSlice.value);

  const [categories, setCategories] = useState<ICategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<string>(TabValues.DETAILS);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const fetchedCategories = await getCategories(true);
        setCategories(fetchedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    if (props.isOpen) {
      fetchCategories();
    }
  }, [props.isOpen]);

  const { useCasePrompt } = props;

  const useCaseTitles = useMemo(() => {
    if (!useCasePrompt.useCaseIds || useCasePrompt.useCaseIds.length === 0) {
      return EMPTY_VALUE;
    }
    return useCasePrompt.useCaseIds
      .map((id) => useCases.find((uc) => uc.id === id)?.title)
      .filter(Boolean)
      .join(", ");
  }, [useCasePrompt.useCaseIds, useCases]);

  const llmModel = getModelByKey(
    models,
    useCasePrompt.llmModel ?? ConstantValues.EMPTY_STRING,
  );

  const knowledgeComponentsLabel = getKnowledgeComponentsLabel(
    useCasePrompt.preselectedKnowledgeComponents || [],
    categories,
  );

  const handleTabClick = (tabKey: string) => {
    setSelectedTab(tabKey);
  };

  return (
    <Dialog
      open={props.isOpen}
      modalType="modal"
      onOpenChange={(_e, data) => {
        if (!data.open) props.onDismiss();
      }}
    >
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={props.onDismiss}
              />
            }
          >
            {LABEL_VIEW}
          </DialogTitle>
          <DialogContent
            className={mergeClasses(
              sharedContentStyles.tabbedDialogContent,
              styles.dialogContent,
            )}
          >
            <DialogTabs
              tabs={[
                { key: TabValues.DETAILS, label: "Details" },
                { key: TabValues.PROMPT, label: "Prompt" },
                { key: TabValues.CONFIGURATION, label: "Configuration" },
              ]}
              activeTab={selectedTab}
              onTabClick={handleTabClick}
            />

            <div
              className={mergeClasses(
                styles.tabContent,
                sharedContentStyles.scrollableContent,
              )}
            >
              {selectedTab === TabValues.DETAILS && (
                <>
                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>Title</div>
                    <div className={styles.fieldValue}>
                      {useCasePrompt.title}
                    </div>
                  </div>

                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>Description</div>
                    <div className={styles.fieldValue}>
                      {useCasePrompt.description}
                    </div>
                  </div>

                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>Use Cases</div>
                    <div className={styles.fieldValue}>{useCaseTitles}</div>
                  </div>

                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>Owner</div>
                    <div className={styles.fieldValue}>
                      {useCasePrompt.owner?.displayName || EMPTY_VALUE}
                    </div>
                  </div>

                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>Created At</div>
                    <div className={styles.fieldValue}>
                      {formatDate(useCasePrompt.createdAt)}
                    </div>
                  </div>

                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>Last Updated</div>
                    <div className={styles.fieldValue}>
                      {formatDate(useCasePrompt.updatedAt)}
                    </div>
                  </div>

                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>Author</div>
                    <div className={styles.fieldValue}>
                      {useCasePrompt.user?.displayName || EMPTY_VALUE}
                    </div>
                  </div>
                </>
              )}

              {selectedTab === TabValues.PROMPT && (
                <>
                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>Prompt</div>
                    <div
                      className={styles.promptContent}
                      dangerouslySetInnerHTML={{
                        __html:
                          useCasePrompt.prompt || ConstantValues.EMPTY_STRING,
                      }}
                    />
                  </div>
                </>
              )}

              {selectedTab === TabValues.CONFIGURATION && (
                <>
                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>Settings</div>
                    <div className={styles.toggleRow}>
                      <div className={styles.toggleItem}>
                        <span className={styles.fieldLabel}>Quals:</span>
                        <span className={styles.fieldValue}>
                          {useCasePrompt.qualsEnabled ? "Yes" : "No"}
                        </span>
                      </div>
                      <div className={styles.toggleItem}>
                        <span className={styles.fieldLabel}>Proposals:</span>
                        <span className={styles.fieldValue}>
                          {useCasePrompt.proposalsEnabled ? "Yes" : "No"}
                        </span>
                      </div>
                      <div className={styles.toggleItem}>
                        <span className={styles.fieldLabel}>Citations:</span>
                        <span className={styles.fieldValue}>
                          {useCasePrompt.citationsEnabled ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>LLM Model</div>
                    <div className={styles.fieldValue}>
                      {llmModel?.displayName || "Default"}
                    </div>
                  </div>

                  <div className={styles.fieldContainer}>
                    <div className={styles.fieldLabel}>
                      Preselected Knowledge Components
                    </div>
                    {categoriesLoading ? (
                      <Spinner size="tiny" label="Loading..." />
                    ) : (
                      <div className={styles.fieldValue}>
                        {knowledgeComponentsLabel || "None"}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
